import { HeaderSelectionCheckbox } from "./HeaderSelectionCheckbox";
import { type StrictlyFrozenSet, setIntersection, setMinus } from "utils/sets";
import { useMemo } from "react";
import type { BulkRequestRecord } from "api/clients/useBulkRequestQuery";
import type { FrozenSetDispatch } from "utils/useFrozenState";
import type { TableRowSelection } from "antd/es/table/interface";

/**
 * Create antd table row selection functionality from raw state.
 * @param selectedRows The current collection of selected rows.
 * @param setSelectedRows Callback to change the collection of selected rows.
 * @param selectableSet Selectable rows (that is, those matching current filter, and not disallowed for communication).
 * @returns Adapted configuration for antd tables.
 */
export function useRowSelection(
    selectedRows: StrictlyFrozenSet<string>,
    setSelectedRows: FrozenSetDispatch<string>,
    selectableSet: StrictlyFrozenSet<string>,
): TableRowSelection<BulkRequestRecord> {
    return useMemo(
        () => ({
            type: "checkbox",
            getCheckboxProps: (record) => ({
                disabled: !record.allowCommunication,
                name: `sel${record.pmsId}`,
                "aria-label": `Toggle select row for ${record.fullName}`,
            }),
            selectedRowKeys: Array.from(selectedRows.values()),
            columnTitle: (
                <HeaderSelectionCheckbox
                    disabled={selectableSet.size === 0}
                    selectedRows={selectedRows}
                    setSelectedRows={setSelectedRows}
                    selectableSet={selectableSet}
                />
            ),
            onChange: (selectedRowKeys) => {
                setSelectedRows((selectedRows) => {
                    // apply new selection, retain filtered-out selection, prohibit selection of rows without communication.
                    // preserveSelectedRowKeys could probably be used as an alternative if we had a non-filtered set of
                    // prohibited (without communication) rows, or examined the full BulkRequestRecord array provided to
                    // onChange (second argument).
                    const filteredOut = setMinus(selectedRows, selectableSet);
                    const filteredIn = setIntersection(selectedRowKeys as string[], selectableSet);

                    // compute union of filteredOut and filteredIn into mutable filteredIn
                    // saved ~0.25ms-1ms vs. setUnion in a 5,000 patient scenario with all selected
                    for (const item of filteredOut) {
                        filteredIn.add(item);
                    }

                    return filteredIn;
                });
            },
        }),
        [selectedRows, setSelectedRows, selectableSet],
    );
}
